import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Billing, Rental } from "@/lib/types";
import { Head, Screens, Stack } from "@/ui/frame/Page";
import { ButtonLink } from "@/ui/Button";
import { Strip, Reading } from "@/ui/Strip";
import { Lamp } from "@/ui/Lamp";
import { ErrorState, Skeleton } from "@/ui/States";
import { Notice, Settle } from "@/ui/Notice";
import { MissingDetailsBanner, StoppedBanner } from "@/features/billing/panels";
import { useRental } from "@/features/billing/rental";
import { canManageBilling } from "@/features/org/roles";
import { useSession } from "@/lib/session";
import { cx } from "@/lib/format";

export const Route = createFileRoute("/_app/o/$orgId/billing")({
  component: BillingLayout,
});

/**
 * The money, as three questions.
 *
 * # WHAT THIS REPLACED, AND WHY NONE OF IT SURVIVED
 *
 * Two tabs: "This month", which showed a running total and the hourly readings
 * behind it, and "Invoices". Both were honest descriptions of billing after the
 * fact, and neither describes what happens now. There is no month in progress
 * to watch, no total running up, and nothing to owe — capacity is rented in
 * advance, and the thing a customer holds is time and a balance.
 *
 * So the subject is the rental, the tabs are rental, credit and documents, and
 * the strip holds the two facts somebody opens this screen for: how long is
 * left, and what is in the balance.
 *
 * # WHY THERE ARE NO LONGER TWO ACTION BUTTONS HERE
 *
 * There were: Extend rental and Buy credit, in the page header, each opening
 * a dialog, on all three tabs. Both have moved to the ordering screen, and
 * what is left in their place is one link to it.
 *
 * The reason is that they were the only two things on this surface that spent
 * money, and they were the two that could not be seen without pressing
 * something. A customer could read every figure the product has about their
 * account without ever finding what it sells. Billing is the record now, and
 * ordering has a screen of its own.
 */
function BillingLayout() {
  const { orgId } = Route.useParams();
  const navigate = useNavigate();
  const { organisations } = useSession();
  const role = organisations.find((o) => o.id === orgId)?.role;
  const rental = useRental(orgId);

  // The old payload, for the one thing it still answers: whether this account
  // can be invoiced at all. An account with no address cannot buy credit,
  // because the tax depends on where they are and an invoice has to name them.
  const account = useQuery({
    queryKey: ["billing", orgId],
    queryFn: ({ signal }) => api.get<Billing>(`/organisations/${orgId}/billing`, undefined, signal),
  });

  const tabs = [
    { to: "/o/$orgId/billing", label: "Rental", exact: true },
    { to: "/o/$orgId/billing/credit", label: "Credit", exact: false },
    { to: "/o/$orgId/billing/invoices", label: "Documents", exact: false },
  ] as const;

  const r = rental.data;
  const ending = r ? r.active && r.days_left <= r.notice_days : false;

  return (
    <>
      <Head
        title="Billing"
        actions={
          canManageBilling(role) ? (
            <ButtonLink tone="primary" to="/o/$orgId/order" params={{ orgId }}>
              Order capacity
            </ButtonLink>
          ) : undefined
        }
        description={
          !r
            ? undefined
            : `Capacity is rented by the month and paid for in advance from credit. A rental ends on the date it says: there is a notice ${r.notice_days} days before and another the day before, services stop on the day, and ${r.delete_after_days} days after that they are deleted. Stored data is kept throughout.`
        }
        screens={
          <Screens
            items={tabs.map((tab) => ({
              label: tab.label,
              to: tab.to,
              params: { orgId },
              exact: tab.exact,
            }))}
          />
        }
      />

      {/* The countdown is the figure, not the balance. A customer with plenty
          of credit and two days left has a problem; one with no credit and
          three weeks left does not — and the layout has to say which of those
          is happening before it says anything else.

          The balance is here too, and also in the header on every screen in
          the product, which is where it is actually asked about: from a
          project, a log, a terminal. */}
      {r && (
        <Strip>
          <Reading
            label={r.active ? "Rental ends in" : "Rental"}
            lamp={
              <Lamp
                state={!r.paid_through ? "idle" : !r.active ? "down" : ending ? "warn" : "up"}
              />
            }
          >
            {r.active ? (
              <span className={ending ? "text-[var(--warning)]" : undefined}>
                {r.days_left} {r.days_left === 1 ? "day" : "days"}
              </span>
            ) : r.paid_through ? (
              <span className="text-[var(--danger)]">Ran out</span>
            ) : (
              "Not started"
            )}
          </Reading>

          <Reading label="Plan">{r.plan}</Reading>

          {r.monthly_micros > 0 && <Reading label="A month">{r.monthly}</Reading>}

          <Reading label="Balance">{r.balance}</Reading>

          {r.monthly_micros > 0 && (
            <Reading label="Covers">
              {r.months_affordable === 0
                ? "no further month"
                : `${r.months_affordable} ${r.months_affordable === 1 ? "month" : "months"}`}
            </Reading>
          )}
        </Strip>
      )}

      <Stack>
        {rental.isLoading && <Skeleton className="h-24 w-full" rounded="lg" />}
        {rental.isError && (
          <ErrorState error={rental.error} onRetry={() => void rental.refetch()} />
        )}

        {r && <RentalBanner rental={r} />}

        {/* An account can still be stopped over one of the usage invoices
            drawn before capacity was rented. Rare, and it stops being
            possible once the last of them is paid — but while it is possible
            it is news about the account rather than about a tab. */}
        {account.data?.stopped_for_payment && <StoppedBanner account={account.data} />}

        {account.data?.identity_missing && !account.data.exempt && (
          <MissingDetailsBanner
            account={account.data}
            onFix={() =>
              void navigate({
                to: "/o/$orgId/billing/invoices",
                params: { orgId },
                search: { edit: "identity" },
              })
            }
          />
        )}

        {r && r.monthly_micros > 0 && r.months_affordable < 1 && (
          <Notice
            title="The balance does not cover another month"
            actions={
              canManageBilling(role) ? (
                <ButtonLink size="dense" to="/o/$orgId/order" params={{ orgId }}>
                  Buy credit
                </ButtonLink>
              ) : undefined
            }
          >
            Capacity is paid for in advance, so a rental cannot be extended past what
            the balance covers.
          </Notice>
        )}

        <Settle>
          <Outlet />
        </Settle>
      </Stack>
    </>
  );
}

/**
 * The one thing that has to be on screen whichever tab is open.
 *
 * Not a banner for a healthy rental: a line saying "everything is fine" on
 * every visit is a line nobody reads, which is the state the real warnings
 * have to arrive into.
 */
function RentalBanner({ rental }: { rental: Rental }) {
  if (rental.active && rental.days_left > rental.notice_days) return null;

  const stopped = Boolean(rental.lapsed_at) || (!rental.active && rental.paid_through);
  const tone = stopped ? "danger" : "caution";

  return (
    <div
      className={cx(
        "rounded-[var(--radius-sm)] border px-4 py-3",
        tone === "danger"
          ? "border-[color-mix(in_oklab,_var(--danger)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--danger)_12%,_transparent)]"
          : "border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)]",
      )}
    >
      <p className="t-strong">
        {stopped
          ? "The services in this account are stopped."
          : rental.active
            ? `The rental ends in ${rental.days_left} ${rental.days_left === 1 ? "day" : "days"}.`
            : "Nothing is rented on this account."}
      </p>
      <p className="t-body mt-0.5">
        {stopped
          ? `The rental ran out and was not extended. Your data has not been touched — extend it and everything starts again where it left off. The services themselves are deleted ${rental.delete_after_days} days after they stopped.`
          : rental.active
            ? "Extend it and nothing changes. Let it run out and the services stop on that date; your data is kept either way."
            : "Put credit on the account and rent a plan, and services can start straight away."}
      </p>
    </div>
  );
}
